/**
 * Centralized date utilities to avoid timezone parsing issues
 * 
 * This module provides safe date parsing and manipulation functions
 * that handle timezone issues consistently across the application.
 */

import { fromZonedTime, toZonedTime } from 'date-fns-tz';

/**
 * Parse a date string safely to avoid timezone issues
 * @param dateString - Date string in YYYY-MM-DD format
 * @returns Date object representing the local date
 * 
 * @example
 * parseDateString('2025-01-27') // Returns Date for January 27, 2025 in local timezone
 */
export function parseDateString(dateString: string): Date {
  const [year, month, day] = dateString.split('-').map(Number);
  return new Date(year, month - 1, day); // month is 0-indexed
}

/**
 * Format a date to YYYY-MM-DD string
 * @param date - Date object to format
 * @returns Date string in YYYY-MM-DD format
 * 
 * @example
 * formatDateString(new Date(2025, 0, 27)) // Returns '2025-01-27'
 */
export function formatDateString(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Check if a date falls on a weekend
 * @param date - Date to check
 * @returns true if the date is Saturday (6) or Sunday (0)
 * 
 * @example
 * isWeekend(new Date(2025, 0, 27)) // Returns false (Monday)
 * isWeekend(new Date(2025, 0, 25)) // Returns true (Saturday)
 */
export function isWeekend(date: Date): boolean {
  const dayOfWeek = date.getDay();
  return dayOfWeek === 0 || dayOfWeek === 6; // Sunday = 0, Saturday = 6
}

/**
 * Check if a date is a weekday (Monday through Friday)
 * @param date - Date to check
 * @returns true if the date is Monday (1) through Friday (5)
 * 
 * @example
 * isWeekday(new Date(2025, 0, 27)) // Returns true (Monday)
 * isWeekday(new Date(2025, 0, 25)) // Returns false (Saturday)
 */
export function isWeekday(date: Date): boolean {
  const dayOfWeek = date.getDay();
  return dayOfWeek >= 1 && dayOfWeek <= 5; // Monday = 1, Friday = 5
}

/**
 * Get the day of the week as a string
 * @param date - Date to check
 * @returns Day name (Sunday, Monday, Tuesday, etc.)
 * 
 * @example
 * getDayName(new Date(2025, 0, 27)) // Returns 'Monday'
 */
export function getDayName(date: Date): string {
  const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  return days[date.getDay()];
}

/**
 * Add days to a date
 * @param date - Base date
 * @param days - Number of days to add (can be negative)
 * @returns New date with days added
 * 
 * @example
 * addDays(new Date(2025, 0, 27), 7) // Returns date 7 days later
 * addDays(new Date(2025, 0, 27), -1) // Returns date 1 day earlier
 */
export function addDays(date: Date, days: number): Date {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
}

/**
 * Get the start of the day (00:00:00)
 * @param date - Date to get start of day for
 * @returns New date set to start of day
 * 
 * @example
 * startOfDay(new Date(2025, 0, 27, 14, 30)) // Returns same date at 00:00:00
 */
export function startOfDay(date: Date): Date {
  const result = new Date(date);
  result.setHours(0, 0, 0, 0);
  return result;
}

/**
 * Get the end of the day (23:59:59.999)
 * @param date - Date to get end of day for
 * @returns New date set to end of day
 * 
 * @example
 * endOfDay(new Date(2025, 0, 27, 14, 30)) // Returns same date at 23:59:59.999
 */
export function endOfDay(date: Date): Date {
  const result = new Date(date);
  result.setHours(23, 59, 59, 999);
  return result;
}

/**
 * Check if two dates are the same day
 * @param date1 - First date
 * @param date2 - Second date
 * @returns true if both dates are on the same day
 * 
 * @example
 * isSameDay(new Date(2025, 0, 27, 10, 0), new Date(2025, 0, 27, 15, 0)) // Returns true
 */
export function isSameDay(date1: Date, date2: Date): boolean {
  return formatDateString(date1) === formatDateString(date2);
}

/**
 * Create a date range between two dates
 * @param startDate - Start date (inclusive)
 * @param endDate - End date (inclusive)
 * @returns Array of dates between start and end
 * 
 * @example
 * createDateRange(parseDateString('2025-01-27'), parseDateString('2025-01-29'))
 * // Returns [Date(2025-01-27), Date(2025-01-28), Date(2025-01-29)]
 */
export function createDateRange(startDate: Date, endDate: Date): Date[] {
  const dates: Date[] = [];
  const current = new Date(startDate);
  
  while (current <= endDate) {
    dates.push(new Date(current));
    current.setDate(current.getDate() + 1);
  }
  
  return dates;
}

/**
 * Parse date string and validate it's a valid date
 * @param dateString - Date string to parse and validate
 * @returns Parsed date if valid, null if invalid
 * 
 * @example
 * parseAndValidateDate('2025-01-27') // Returns Date object
 * parseAndValidateDate('invalid-date') // Returns null
 */
export function parseAndValidateDate(dateString: string): Date | null {
  try {
    const date = parseDateString(dateString);
    // Check if the parsed date is valid
    if (isNaN(date.getTime())) {
      return null;
    }
    // Check if the date string matches the parsed date (catches invalid dates like 2025-02-30)
    if (formatDateString(date) !== dateString) {
      return null;
    }
    return date;
  } catch {
    return null;
  }
}

/**
 * Business hours configuration interface
 */
export interface BusinessHours {
  startHour: number;
  endHour: number;
  lunchStartHour: number;
  lunchEndHour: number;
  exclusionStartHour?: number;
  exclusionEndHour?: number;
}

/**
 * Generate business hours configuration for a specific user
 * @param email - User email to get specific business hours for
 * @param extendedHours - Whether to use extended hours (8am-6pm vs 9am-5pm)
 * @returns Business hours configuration
 */
export function getBusinessHours(email?: string, extendedHours: boolean = false): BusinessHours {
  if (email === "miclainkeffeler@westtn.consulting") {
    return {
      startHour: 8,  // 8am CST
      endHour: 18,   // 6pm CST
      lunchStartHour: 11.5, // 11:30am CST
      lunchEndHour: 13,     // 1:00pm CST
      exclusionStartHour: 10, // 10am CST
      exclusionEndHour: 11    // 11am CST
    };
  } else {
    return {
      startHour: extendedHours ? 8 : 9,  // 8am or 9am CST
      endHour: extendedHours ? 18 : 17,  // 6pm or 5pm CST
      lunchStartHour: 11.5, // 11:30am CST
      lunchEndHour: 13      // 1:00pm CST
    };
  }
}

/**
 * Generate time slots for a specific date in CST, then convert to UTC
 * @param date - Date to generate slots for
 * @param email - User email for specific business hours
 * @param extendedHours - Whether to use extended hours
 * @param slotDuration - Duration of each slot in minutes
 * @param bufferMinutes - Buffer time between slots in minutes
 * @returns Array of time slots in UTC
 */
export function generateBusinessTimeSlots(
  date: Date,
  email?: string,
  extendedHours: boolean = false,
  slotDuration: number = 30,
  bufferMinutes: number = 15
): Array<{ start: string; end: string; available: boolean; isLunchSlot?: boolean }> {
  const timezone = "America/Chicago";
  const businessHours = getBusinessHours(email, extendedHours);
  const slots: Array<{ start: string; end: string; available: boolean; isLunchSlot?: boolean }> = [];

  // Create a date object for the specified date in CST
  const year = date.getFullYear();
  const month = date.getMonth();
  const day = date.getDate();
  
  // Generate lunch slot first
  const lunchStartCST = new Date(year, month, day, Math.floor(businessHours.lunchStartHour), (businessHours.lunchStartHour % 1) * 60, 0, 0);
  const lunchEndCST = new Date(year, month, day, businessHours.lunchEndHour, 0, 0, 0);
  
  const lunchSlot = {
    start: fromZonedTime(lunchStartCST, timezone).toISOString(),
    end: fromZonedTime(lunchEndCST, timezone).toISOString(),
    available: true,
    isLunchSlot: true
  };
  slots.push(lunchSlot);

  // Generate regular time slots
  let currentHour = businessHours.startHour;
  
  while (currentHour < businessHours.endHour) {
    // Skip lunch time
    if (currentHour >= businessHours.lunchStartHour && currentHour < businessHours.lunchEndHour) {
      currentHour = businessHours.lunchEndHour;
      continue;
    }
    
    // Skip exclusion time (for Miclain)
    if (businessHours.exclusionStartHour && businessHours.exclusionEndHour &&
        currentHour >= businessHours.exclusionStartHour && currentHour < businessHours.exclusionEndHour) {
      currentHour = businessHours.exclusionEndHour;
      continue;
    }
    
    // Create slot
    const slotStartCST = new Date(year, month, day, Math.floor(currentHour), (currentHour % 1) * 60, 0, 0);
    const slotEndCST = new Date(slotStartCST.getTime() + slotDuration * 60 * 1000);
    
    // Only add slot if it doesn't go beyond end time
    if (slotEndCST.getHours() <= businessHours.endHour) {
      slots.push({
        start: fromZonedTime(slotStartCST, timezone).toISOString(),
        end: fromZonedTime(slotEndCST, timezone).toISOString(),
        available: true
      });
    }
    
    // Move to next slot (add buffer time)
    currentHour += (slotDuration + bufferMinutes) / 60;
  }

  return slots;
}
